import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { toast } from 'react-toastify';
import {
  useBrandRulesQuery,
  useUpdateBrandRulesMutation,
} from '../../services/brandRulesService/__generated__/queries.generated';
import { Button } from '../../components/button';
import SwitchComponent from '../../components/switch';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Field,
  FormGrid,
  Input,
  LoadingBlock,
  Select,
} from '../../components/ui';
import {
  CancellationRulesForm,
  FormErrors,
  LATE_FEE_MODES,
  LateFeeMode,
  SHORTEN_MODES,
  ShortenMode,
  cancellationRulesOf,
  changedCancellationRules,
  exampleRefunds,
  fromForm,
  toForm,
} from './cancellationRulesAdmin';

const ToggleRow = styled.label`
  ${tw`flex items-start justify-between gap-4 py-3 cursor-pointer`}
`;
const ToggleText = styled.div`
  ${tw`min-w-0`}
`;
const ToggleTitle = styled.div`
  ${tw`text-sm font-medium`}
  color: var(--color-text-primary);
`;
const ToggleHint = styled.div`
  ${tw`text-xs mt-0.5`}
  color: var(--color-text-secondary);
`;
const Example = styled.ul`
  ${tw`text-sm mt-4 pl-4 list-disc`}
  color: var(--color-text-secondary);
`;

function isStale(error: unknown): boolean {
  const graphQLErrors = (error as { graphQLErrors?: Array<{ extensions?: { code?: string } }> })
    ?.graphQLErrors;
  return Boolean(graphQLErrors?.some(e => e.extensions?.code === 'BRAND_RULES_STALE'));
}

/**
 * Spec 014 Phase 3: this brand's cancellation and shortening rules, on
 * `/admin/brand-settings`. The server computes every refund from them. A
 * booking keeps the rules it was made under, so a change reaches only trips
 * booked after it is saved.
 */
export function CancellationRulesSection({ brandName }: { brandName: string }) {
  const { data, loading, error, refetch } = useBrandRulesQuery({ fetchPolicy: 'network-only' });
  const [updateBrandRules, { loading: saving }] = useUpdateBrandRulesMutation({
    refetchQueries: ['BrandRulesHistory'],
  });

  const loaded = useMemo(() => cancellationRulesOf(data?.brandRules?.bookingRules), [data]);
  const [form, setForm] = useState<CancellationRulesForm>(() => toForm(loaded));
  useEffect(() => setForm(toForm(loaded)), [loaded]);

  const parsed = fromForm(form);
  const errors: FormErrors = parsed.errors ?? {};
  const changes = parsed.rules ? changedCancellationRules(loaded, parsed.rules) : {};
  const dirty = Object.keys(changes).length > 0;
  const example = exampleRefunds(parsed.rules ?? loaded);

  const set = <K extends keyof CancellationRulesForm>(key: K, value: CancellationRulesForm[K]) =>
    setForm(current => ({ ...current, [key]: value }));

  const lateUnit = LATE_FEE_MODES.find(m => m.value === form.lateFeeMode)?.unit ?? '';
  const shortenUnit = SHORTEN_MODES.find(m => m.value === form.shortenMode)?.unit ?? '';

  const save = async () => {
    if (!data?.brandRules || !dirty || !parsed.rules) return;
    try {
      await updateBrandRules({
        variables: { input: { version: data.brandRules.version, bookingRules: changes } },
      });
      toast.success('Saved. New bookings use these rules within about a minute.');
      await refetch();
    } catch (e) {
      if (isStale(e)) {
        toast.error(
          'Someone else changed these rules. The latest values are loaded; check them and save again.',
        );
        await refetch();
        return;
      }
      toast.error((e as Error).message || 'Could not save cancellation rules');
    }
  };

  return (
    <Card>
      <CardHeader
        title="Cancellation rules"
        description={`What guests on ${brandName} get back when they cancel or shorten a trip. Changes apply to trips booked after you save; trips already booked keep the rules they were booked under.`}
      />
      {loading && <LoadingBlock label="Loading cancellation rules" />}
      {error && (
        <CardBody>
          <Alert tone="error" title="Could not load cancellation rules">
            {error.message}
          </Alert>
        </CardBody>
      )}
      {!loading && !error && (
        <CardBody>
          <FormGrid $cols={2}>
            <Field
              label="Free cancellation until (hours before pickup)"
              htmlFor="cancel-advance"
              hint="Before this, guests pay only the processing fee."
              error={errors.freeCancellationAdvanceHours}
            >
              <Input
                id="cancel-advance"
                inputMode="decimal"
                value={form.freeCancellationAdvanceHours}
                onChange={e => set('freeCancellationAdvanceHours', e.target.value)}
              />
            </Field>
            <Field
              label="Grace period after booking (hours)"
              htmlFor="cancel-grace"
              hint="Free cancellation right after booking. 0 turns it off."
              error={errors.gracePeriodHours}
            >
              <Input
                id="cancel-grace"
                inputMode="decimal"
                value={form.gracePeriodHours}
                onChange={e => set('gracePeriodHours', e.target.value)}
              />
            </Field>
            <Field
              label="Processing fee (%)"
              htmlFor="cancel-processing"
              hint="Kept from every free cancellation or shortening."
              error={errors.processingFeePercent}
            >
              <Input
                id="cancel-processing"
                inputMode="decimal"
                value={form.processingFeePercent}
                onChange={e => set('processingFeePercent', e.target.value)}
              />
            </Field>
            <div />
            <Field label="Late cancellation fee" htmlFor="cancel-late-mode">
              <Select
                id="cancel-late-mode"
                value={form.lateFeeMode}
                onChange={e => set('lateFeeMode', e.target.value as LateFeeMode)}
              >
                {LATE_FEE_MODES.map(m => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label={`Amount (${lateUnit})`}
              htmlFor="cancel-late-value"
              hint="Never more than the trip total."
              error={errors.lateFeeValue}
            >
              <Input
                id="cancel-late-value"
                inputMode="decimal"
                value={form.lateFeeValue}
                onChange={e => set('lateFeeValue', e.target.value)}
              />
            </Field>
            <Field label="Shortening penalty" htmlFor="cancel-shorten-mode">
              <Select
                id="cancel-shorten-mode"
                value={form.shortenMode}
                onChange={e => set('shortenMode', e.target.value as ShortenMode)}
              >
                {SHORTEN_MODES.map(m => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </Field>
            {form.shortenMode !== 'noRefundStartedPeriod' ? (
              <Field
                label={`Amount (${shortenUnit})`}
                htmlFor="cancel-shorten-value"
                hint="Never more than the removed days' price. Not charged before the free cancellation deadline."
                error={errors.shortenValue}
              >
                <Input
                  id="cancel-shorten-value"
                  inputMode="decimal"
                  value={form.shortenValue}
                  onChange={e => set('shortenValue', e.target.value)}
                />
              </Field>
            ) : (
              <div />
            )}
          </FormGrid>
          <ToggleRow>
            <ToggleText>
              <ToggleTitle>Also keep the processing fee on late cancellations</ToggleTitle>
              <ToggleHint>
                Off: a late cancellation pays the late fee only. On: the late fee and the processing
                fee.
              </ToggleHint>
            </ToggleText>
            <SwitchComponent
              isActive={form.stackLateFeeWithProcessingFee}
              onChange={e => set('stackLateFeeWithProcessingFee', e.target.checked)}
              inputTestId="cancel-stack-fee"
              ariaLabel="Also keep the processing fee on late cancellations"
            />
          </ToggleRow>
          <Example data-testid="cancellation-example">
            <li>{example.early}</li>
            <li>{example.late}</li>
          </Example>
        </CardBody>
      )}
      <CardFooter>
        <Button
          theme="filled"
          text="Save cancellation rules"
          onClick={save}
          isDisabled={!dirty || saving || !parsed.rules}
          isLoading={saving}
        />
      </CardFooter>
    </Card>
  );
}

export default CancellationRulesSection;
